import { MotionConfig, fadeUp, motion, stagger } from '@/lib/motion';
import { ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { Button } from '@/components/ui/button';
import { Seo } from '@/lib/seo';


const ROWS = [
  { feature: 'Pricing model', us: 'Flat monthly — no per-minute billing', them: 'Per-minute plans (~$1.75–$2.50/min)' },
  { feature: '24/7 coverage', us: 'All plans, included', them: 'Available — higher cost' },
  { feature: 'Setup time', us: 'Under 10 minutes', them: '2–5 business days' },
  { feature: 'AI-powered', us: 'Yes — LLM natural conversation', them: 'Human virtual receptionists' },
  { feature: 'Website chat', us: 'Included — same knowledge base', them: 'Not included' },
  { feature: 'Knowledge base training', us: 'Upload your docs instantly', them: 'Script creation with Ruby team' },
  { feature: 'Call transcripts', us: 'Full transcript + AI summary', them: 'Call notes (human-written)' },
  { feature: 'Predictable billing', us: 'Yes — flat monthly', them: 'No — varies with call volume' },
  { feature: 'Starting price', us: 'From $39/month', them: 'From ~$235/month (50 min)' },
];

export function VsRubyPage(): JSX.Element {
  const brand = useBrand();
  return (
    <MotionConfig reducedMotion="user">
      <Seo
        title={`${brand.displayName} vs Ruby Receptionists — Which is Right for Your Business?`}
        description={`${brand.displayName} vs Ruby: AI receptionist vs human virtual receptionist. Compare pricing, features, and setup for your business.`}
        path="/compare/vs-ruby"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/15 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
        </div>
        <div className="container py-16 sm:py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="mx-auto max-w-3xl text-center"
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--brand-accent)]"
            >
              Comparison
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl"
            >
              {brand.displayName} vs Ruby Receptionists
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-lg text-muted-foreground"
            >
              Ruby uses human virtual receptionists billed by the minute. {brand.displayName} is AI — flat monthly, 24/7, trained on your business.
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
            >
              <Button asChild size="lg">
                <Link to="/signup">
                  Get started with {brand.displayName} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 sm:py-20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-center text-2xl font-bold">Feature comparison</h2>
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="py-4 pl-6 pr-4 text-left font-semibold">Feature</th>
                    <th className="py-4 px-4 text-left font-semibold text-[var(--brand-accent)]">
                      {brand.displayName}
                    </th>
                    <th className="py-4 pl-4 pr-6 text-left font-semibold text-muted-foreground">
                      Ruby Receptionists
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(({ feature, us, them }, i) => (
                    <tr key={feature} className={i < ROWS.length - 1 ? 'border-b border-border/50' : ''}>
                      <td className="py-4 pl-6 pr-4 font-medium">{feature}</td>
                      <td className="py-4 px-4 text-[var(--brand-accent)]">{us}</td>
                      <td className="py-4 pl-4 pr-6 text-muted-foreground">{them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* When to choose each */}
      <section className="border-t border-border py-16 sm:py-20">
        <div className="container">
          <div className="mx-auto grid max-w-4xl gap-8 sm:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border-2 border-[var(--brand-accent)]/40 bg-background p-7"
            >
              <h3 className="text-lg font-bold text-[var(--brand-accent)]">
                Choose {brand.displayName} if…
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  'You want predictable flat monthly billing',
                  '24/7 coverage without extra cost',
                  'Setup in minutes, not days',
                  'Calls are primarily appointments, FAQs, and intake',
                  'You also want website chat from the same system',
                  'Budget under $200/month',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button asChild className="mt-7 w-full">
                <Link to="/signup">Get started</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl border border-border bg-muted/20 p-7"
            >
              <h3 className="text-lg font-bold text-muted-foreground">
                Consider Ruby if…
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  'Your calls require human empathy and nuanced judgment',
                  'You have highly unpredictable call types',
                  'You need bilingual human agents',
                  'Budget is not the primary constraint ($500+/month)',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
